import { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { Box, TextField } from '@mui/material'
import { Typography, Alert } from '@carto/meridian-ds/components'
import { Connection, ProviderType } from 'features/connections'
import DocLink from 'features/common/ui/utils/DocLink'
import { DOCS_TEMP_DATA_IN_WORKFLOWS } from 'app/constants/urls'
import {
  DEFAULT_EXTENSIONS_LOCATION_NAME,
  DEFAULT_TEMP_LOCATION_NAME,
  DEFAULT_WORKSPACE_LOCATION_NAME
} from '../../utils'

interface WorkflowsTempFormProps {
  form: Omit<UseFormReturn, 'handleSubmit' | 'formState'>
  connection?: Connection
  provider: ProviderType
}

const tempLocationsName = 'tempLocations'
const workspaceLocationsName = 'workspaceLocations'
const extensionsLocationsName = 'extensionsLocations'

function WorkflowsTempForm({ form, connection, provider }: WorkflowsTempFormProps) {
  const { register, setValue, watch } = form
  const viewerCredentials = watch('viewerCredentials') || false

  const isEditing = Boolean(connection?.id)
  const isBigQuery = provider === ProviderType.BIGQUERY
  const isSnowflake = provider === ProviderType.SNOWFLAKE

  useEffect(() => {
    if (isEditing) {
      setValue(tempLocationsName, connection?.tempLocations?.[0]?.name || '', { shouldDirty: true })
      setValue(workspaceLocationsName, connection?.workspaceLocations?.[0]?.name || '', { shouldDirty: true })
      setValue(extensionsLocationsName, connection?.extensionsLocations?.[0]?.name || '', { shouldDirty: true })
    }
  }, [connection?.tempLocations, connection?.workspaceLocations, connection?.extensionsLocations, isEditing, setValue])

  return (
    <>
      <Box mt={2}>
        <TextField
          size="small"
          label={<FormattedMessage id="connections.create.advanced.tempLocations.title" />}
          id={tempLocationsName}
          inputProps={register(tempLocationsName, { required: false })}
          placeholder={DEFAULT_TEMP_LOCATION_NAME}
          helperText={
            <FormattedMessage
              id={
                isBigQuery
                  ? 'connections.create.advanced.tempLocations.descriptionBigQuery'
                  : 'connections.create.advanced.tempLocations.descriptionGeneric'
              }
              values={{
                a: (content: string) => <DocLink docUrl={DOCS_TEMP_DATA_IN_WORKFLOWS}>{content}</DocLink>,
                code: (content: string) => (
                  <Typography variant="code3" color="textSecondary">
                    {content}
                  </Typography>
                )
              }}
            />
          }
        />
        {viewerCredentials ? (
          <Alert severity="neutral">
            <FormattedMessage id="connections.create.advanced.alert" />
          </Alert>
        ) : null}
      </Box>
      <Box mt={2}>
        <TextField
          size="small"
          label={<FormattedMessage id="connections.create.advanced.workspaceLocations.title" />}
          id={workspaceLocationsName}
          inputProps={register(workspaceLocationsName, { required: false })}
          placeholder={DEFAULT_WORKSPACE_LOCATION_NAME}
          helperText={
            <FormattedMessage
              id={
                isBigQuery
                  ? 'connections.create.advanced.workspaceLocations.descriptionBigQuery'
                  : 'connections.create.advanced.workspaceLocations.descriptionGeneric'
              }
              values={{
                code: (content: string) => (
                  <Typography variant="code3" color="textSecondary">
                    {content}
                  </Typography>
                )
              }}
            />
          }
        />
        {viewerCredentials ? (
          <Alert severity="neutral">
            <FormattedMessage id="connections.create.advanced.alert" />
          </Alert>
        ) : null}
      </Box>
      {isBigQuery || isSnowflake ? (
        <Box mt={2}>
          <TextField
            size="small"
            label={<FormattedMessage id="connections.create.advanced.extensionsLocations.title" />}
            id={extensionsLocationsName}
            inputProps={register(extensionsLocationsName, { required: false })}
            placeholder={DEFAULT_EXTENSIONS_LOCATION_NAME}
            helperText={
              <FormattedMessage
                id={
                  isBigQuery
                    ? 'connections.create.advanced.extensionsLocations.descriptionBigQuery'
                    : 'connections.create.advanced.extensionsLocations.descriptionGeneric'
                }
                values={{
                  code: (content: string) => (
                    <Typography variant="code3" color="textSecondary">
                      {content}
                    </Typography>
                  )
                }}
              />
            }
          />
        </Box>
      ) : null}
    </>
  )
}

export default WorkflowsTempForm
